import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// HELPERS
// ============================================================

const getErrorMessage = (
  error,
  fallback = "Something went wrong"
) => {
  return (
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};


// ============================================================
// BILLING CONFIGURATION
// ============================================================

export const fetchBillingConfigurationThunk =
  createAsyncThunk(
    "panchayathBilling/fetchBillingConfiguration",
    async (_, { rejectWithValue }) => {
      try {
        const response = await api.get(
          "/api/v1/billing-configuration"
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to load billing configuration"
          );
        }

        return response.data?.data || null;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load billing configuration"
          )
        );
      }
    }
  );

export const updateSubscriptionBillingConfigThunk =
  createAsyncThunk(
    "panchayathBilling/updateSubscriptionBillingConfig",
    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const response = await api.put(
          "/api/v1/billing-configuration/subscription",
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to update subscription GST"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to update subscription GST"
          )
        );
      }
    }
  );

export const updateBillingSchedulerThunk =
  createAsyncThunk(
    "panchayathBilling/updateBillingScheduler",
    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const response = await api.put(
          "/api/v1/billing-configuration/scheduler",
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to update subscription scheduler"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to update subscription scheduler"
          )
        );
      }
    }
  );

export const updateInvoiceBillingConfigThunk =
  createAsyncThunk(
    "panchayathBilling/updateInvoiceBillingConfig",
    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const response = await api.put(
          "/api/v1/billing-configuration/invoice",
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to update invoice prefix"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to update invoice prefix"
          )
        );
      }
    }
  );

export const updateManualTaxBillingConfigThunk =
  createAsyncThunk(
    "panchayathBilling/updateManualTaxBillingConfig",
    async (
      {
        category,
        payload,
      },
      { rejectWithValue }
    ) => {
      try {
        const response = await api.put(
          `/api/v1/billing-configuration/manual-tax/${category}`,
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to update manual bill tax configuration"
          );
        }

        return {
          category,
          ...response.data,
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to update manual bill tax configuration"
          )
        );
      }
    }
  );


// ============================================================
// BILLING CONFIGURATION - ADDITIONAL SETTINGS
// ============================================================

export const addBillingSettingThunk =
  createAsyncThunk(
    "panchayathBilling/addBillingSetting",
    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const response = await api.post(
          "/api/v1/billing-configuration/settings",
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to add setting"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to add setting"
          )
        );
      }
    }
  );

export const updateBillingSettingThunk =
  createAsyncThunk(
    "panchayathBilling/updateBillingSetting",
    async (
      {
        settingId,
        payload,
      },
      { rejectWithValue }
    ) => {
      try {
        const response = await api.put(
          `/api/v1/billing-configuration/settings/${settingId}`,
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to update setting"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to update setting"
          )
        );
      }
    }
  );

// ============================================================
// GET LOOKUPS
//
// GET /api/v1/panchayath-billing/lookups
// ============================================================

export const fetchBillingLookupsThunk =
  createAsyncThunk(
    "panchayathBilling/lookups",
    async (_, { rejectWithValue }) => {
      try {
        const response = await api.get(
          "/api/v1/panchayath-billing/lookups"
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to load billing lookups"
          );
        }

        return (
          response.data?.data || {
            categories: [],
            manual_categories: [],
            payment_modes: [],
            statuses: [],
          }
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load billing lookups"
          )
        );
      }
    }
  );

// ============================================================
// GET BILL LIST
//
// GET /api/v1/panchayath-billing
// ============================================================

export const fetchPanchayathBillsThunk =
  createAsyncThunk(
    "panchayathBilling/list",
    async (
      filters = {},
      { rejectWithValue }
    ) => {
      try {
        const params = {
          page:
            filters.page || 1,

          pageSize:
            filters.pageSize || 10,

          pan_id:
            filters.pan_id || "",

          category:
            filters.category || "",

          status:
            filters.status || "",

          generated_type:
            filters.generated_type || "",

          from_date:
            filters.from_date || "",

          to_date:
            filters.to_date || "",

          search:
            filters.search || "",
        };

        const response = await api.get(
          "/api/v1/panchayath-billing",
          {
            params,
          }
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to load bills"
          );
        }

        return {
          rows:
            response.data?.data || [],

          pagination:
            response.data?.pagination || {
              page: 1,
              pageSize: 10,
              total: 0,
              totalPages: 0,
            },
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load bills"
          )
        );
      }
    }
  );

// ============================================================
// GET BILL BY ID
// ============================================================

export const fetchPanchayathBillByIdThunk =
  createAsyncThunk(
    "panchayathBilling/getById",
    async (
      billId,
      { rejectWithValue }
    ) => {
      try {
        const response = await api.get(
          `/api/v1/panchayath-billing/${billId}`
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to load bill"
          );
        }

        return response.data?.data || null;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load bill"
          )
        );
      }
    }
  );

// ============================================================
// CREATE MANUAL BILL
// ============================================================

export const createManualBillThunk =
  createAsyncThunk(
    "panchayathBilling/createManual",
    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const response = await api.post(
          "/api/v1/panchayath-billing/manual",
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to create manual bill"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to create manual bill"
          )
        );
      }
    }
  );

// ============================================================
// GENERATE AUTOMATIC SUBSCRIPTION BILLS
// ============================================================

export const generateSubscriptionBillsThunk =
  createAsyncThunk(
    "panchayathBilling/generateSubscriptions",
    async (_, { rejectWithValue }) => {
      try {
        const response = await api.post(
          "/api/v1/panchayath-billing/generate-subscriptions"
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to generate subscription bills"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to generate subscription bills"
          )
        );
      }
    }
  );


// ============================================================
// GENERATION HISTORY
//
// GET /api/v1/panchayath-billing/generation-history
// ============================================================

export const fetchGenerationHistoryThunk =
  createAsyncThunk(
    "panchayathBilling/generationHistory",
    async (
      filters = {},
      { rejectWithValue }
    ) => {
      try {
        const params = {
          page:
            filters.page || 1,

          pageSize:
            filters.pageSize || 10,

          search:
            filters.search || "",

          source:
            filters.source || "",

          action:
            filters.action || "",

          pan_id:
            filters.pan_id || "",

          from_date:
            filters.from_date || "",

          to_date:
            filters.to_date || "",
        };

        const response = await api.get(
          "/api/v1/panchayath-billing/generation-history",
          {
            params,
          }
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to load generation history"
          );
        }

        return {
          latestCron:
            response.data?.data
              ?.latest_cron ||
            null,

          rows:
            response.data?.data
              ?.rows ||
            [],

          pagination:
            response.data?.pagination || {
              page: 1,
              pageSize: 10,
              total: 0,
              totalPages: 0,
            },
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load generation history"
          )
        );
      }
    }
  );

// ============================================================
// SAVE INVOICE
//
// PUT multipart/form-data
// /api/v1/panchayath-billing/:billId/invoice
//
// One optional invoice file:
// invoice_file
// ============================================================

export const saveBillInvoiceThunk =
  createAsyncThunk(
    "panchayathBilling/saveInvoice",
    async (
      {
        billId,
        payload,
      },
      { rejectWithValue }
    ) => {
      try {
        const formData =
          new FormData();

        formData.append(
          "invoice_number",
          payload.invoice_number ||
            ""
        );

        formData.append(
          "invoice_amount",
          String(
            payload.invoice_amount ??
              ""
          )
        );

        formData.append(
          "invoice_date",
          payload.invoice_date ||
            ""
        );

        if (
          payload.invoice_file
        ) {
          formData.append(
            "invoice_file",
            payload.invoice_file
          );
        }

        const response =
          await api.put(
            `/api/v1/panchayath-billing/${billId}/invoice`,
            formData
          );

        if (
          !response.data?.success
        ) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to save invoice"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to save invoice"
          )
        );
      }
    }
  );

// ============================================================
// GET RECEIPTS
// ============================================================

export const fetchBillReceiptsThunk =
  createAsyncThunk(
    "panchayathBilling/receipts",
    async (
      billId,
      { rejectWithValue }
    ) => {
      try {
        const response = await api.get(
          `/api/v1/panchayath-billing/${billId}/receipts`
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to load receipt history"
          );
        }

        return response.data?.data || {
          bill: null,
          receipts: [],
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load receipt history"
          )
        );
      }
    }
  );

// ============================================================
// ADD RECEIPT
// ============================================================

export const addBillReceiptThunk =
  createAsyncThunk(
    "panchayathBilling/addReceipt",
    async (
      {
        billId,
        payload,
      },
      { rejectWithValue }
    ) => {
      try {
        const response = await api.post(
          `/api/v1/panchayath-billing/${billId}/receipts`,
          payload
        );

        if (!response.data?.success) {
          return rejectWithValue(
            response.data?.message ||
              "Failed to save payment"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to save payment"
          )
        );
      }
    }
  );

// ============================================================
// INITIAL STATE
// ============================================================

const initialState = {
  rows: [],

  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },

  lookups: {
    categories: [],
    manual_categories: [],
    payment_modes: [],
    statuses: [],
  },

  generationHistory: {
    latestCron: null,
    rows: [],
  },

  generationHistoryPagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },

  generationHistoryLoading: false,

  selectedBill: null,

  receiptHistory: {
    bill: null,
    receipts: [],
  },

  loading: false,

  detailLoading: false,

  lookupLoading: false,

  receiptLoading: false,

  savingManualBill: false,

  savingInvoice: false,

  savingReceipt: false,

  generating: false,

  // ==========================================================
  // BILLING CONFIGURATION
  // ==========================================================
  billingConfiguration: null,

  billingConfigurationLoading: false,

  savingSubscriptionConfig: false,

  savingSchedulerConfig: false,

  savingInvoiceConfig: false,

  savingManualTaxConfig: {},

  addingBillingSetting: false,

  savingAdditionalSettings: {},

  billingConfigurationError: null,

  error: null,
};

// ============================================================
// SLICE
// ============================================================

const panchayathBillingSlice =
  createSlice({
    name: "panchayathBilling",

    initialState,

    reducers: {
      clearBillingError:
        (state) => {
          state.error = null;
        },

      clearSelectedBill:
        (state) => {
          state.selectedBill = null;
        },

      clearReceiptHistory:
        (state) => {
          state.receiptHistory = {
            bill: null,
            receipts: [],
          };
        },

      resetPanchayathBilling:
        () => initialState,

      clearBillingConfigurationError:
        (state) => {
          state.billingConfigurationError = null;
        },
    },

    extraReducers: (builder) => {
      builder

        // ====================================================
        // LOOKUPS
        // ====================================================

        .addCase(
          fetchBillingLookupsThunk.pending,
          (state) => {
            state.lookupLoading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchBillingLookupsThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.lookupLoading = false;

            state.lookups =
              action.payload;
          }
        )

        .addCase(
          fetchBillingLookupsThunk.rejected,
          (
            state,
            action
          ) => {
            state.lookupLoading = false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // LIST
        // ====================================================

        .addCase(
          fetchPanchayathBillsThunk.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchPanchayathBillsThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.rows =
              action.payload.rows;

            state.pagination =
              action.payload.pagination;
          }
        )

        .addCase(
          fetchPanchayathBillsThunk.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // GET BY ID
        // ====================================================

        .addCase(
          fetchPanchayathBillByIdThunk.pending,
          (state) => {
            state.detailLoading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchPanchayathBillByIdThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.detailLoading = false;

            state.selectedBill =
              action.payload;
          }
        )

        .addCase(
          fetchPanchayathBillByIdThunk.rejected,
          (
            state,
            action
          ) => {
            state.detailLoading = false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // CREATE MANUAL
        // ====================================================

        .addCase(
          createManualBillThunk.pending,
          (state) => {
            state.savingManualBill = true;
            state.error = null;
          }
        )

        .addCase(
          createManualBillThunk.fulfilled,
          (state) => {
            state.savingManualBill = false;
          }
        )

        .addCase(
          createManualBillThunk.rejected,
          (
            state,
            action
          ) => {
            state.savingManualBill = false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // GENERATE
        // ====================================================

        .addCase(
          generateSubscriptionBillsThunk.pending,
          (state) => {
            state.generating = true;
            state.error = null;
          }
        )

        .addCase(
          generateSubscriptionBillsThunk.fulfilled,
          (state) => {
            state.generating = false;
          }
        )

        .addCase(
          generateSubscriptionBillsThunk.rejected,
          (
            state,
            action
          ) => {
            state.generating = false;

            state.error =
              action.payload;
          }
        )


        // ====================================================
        // GENERATION HISTORY
        // ====================================================

        .addCase(
          fetchGenerationHistoryThunk.pending,
          (state) => {
            state.generationHistoryLoading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchGenerationHistoryThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.generationHistoryLoading = false;

            state.generationHistory = {
              latestCron:
                action.payload
                  ?.latestCron ||
                null,

              rows:
                action.payload
                  ?.rows ||
                [],
            };

            state.generationHistoryPagination =
              action.payload
                ?.pagination || {
                page: 1,
                pageSize: 10,
                total: 0,
                totalPages: 0,
              };
          }
        )

        .addCase(
          fetchGenerationHistoryThunk.rejected,
          (
            state,
            action
          ) => {
            state.generationHistoryLoading = false;
            state.error =
              action.payload;
          }
        )

        // ====================================================
        // SAVE INVOICE
        // ====================================================

        .addCase(
          saveBillInvoiceThunk.pending,
          (state) => {
            state.savingInvoice = true;
            state.error = null;
          }
        )

        .addCase(
          saveBillInvoiceThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.savingInvoice = false;

            if (
              state.selectedBill &&
              action.payload?.data
            ) {
              state.selectedBill = {
                ...state.selectedBill,
                ...action.payload.data,
              };
            }
          }
        )

        .addCase(
          saveBillInvoiceThunk.rejected,
          (
            state,
            action
          ) => {
            state.savingInvoice = false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // RECEIPTS
        // ====================================================

        .addCase(
          fetchBillReceiptsThunk.pending,
          (state) => {
            state.receiptLoading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchBillReceiptsThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.receiptLoading = false;

            state.receiptHistory =
              action.payload;
          }
        )

        .addCase(
          fetchBillReceiptsThunk.rejected,
          (
            state,
            action
          ) => {
            state.receiptLoading = false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // ADD RECEIPT
        // ====================================================

        .addCase(
          addBillReceiptThunk.pending,
          (state) => {
            state.savingReceipt = true;
            state.error = null;
          }
        )

        .addCase(
          addBillReceiptThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.savingReceipt = false;

            if (
              state.selectedBill &&
              action.payload?.data
            ) {
              state.selectedBill = {
                ...state.selectedBill,

                total_received:
                  action.payload.data
                    .total_received,

                balance_amount:
                  action.payload.data
                    .balance_amount,

                status:
                  action.payload.data
                    .status,
              };
            }
          }
        )

        .addCase(
          addBillReceiptThunk.rejected,
          (
            state,
            action
          ) => {
            state.savingReceipt = false;

            state.error =
              action.payload;
          }
        )

        // ====================================================
        // BILLING CONFIGURATION - GET
        // ====================================================

        .addCase(
          fetchBillingConfigurationThunk.pending,
          (state) => {
            state.billingConfigurationLoading = true;
            state.billingConfigurationError = null;
          }
        )

        .addCase(
          fetchBillingConfigurationThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.billingConfigurationLoading = false;
            state.billingConfiguration =
              action.payload;
          }
        )

        .addCase(
          fetchBillingConfigurationThunk.rejected,
          (
            state,
            action
          ) => {
            state.billingConfigurationLoading = false;
            state.billingConfigurationError =
              action.payload;
          }
        )

        // ====================================================
        // BILLING CONFIGURATION - SUBSCRIPTION
        // ====================================================

        .addCase(
          updateSubscriptionBillingConfigThunk.pending,
          (state) => {
            state.savingSubscriptionConfig = true;
            state.billingConfigurationError = null;
          }
        )

        .addCase(
          updateSubscriptionBillingConfigThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.savingSubscriptionConfig = false;

            const value =
              action.payload?.data?.gst_percentage;

            if (value !== undefined) {
              state.billingConfiguration =
                state.billingConfiguration || {};

              state.billingConfiguration.subscription = {
                ...(state.billingConfiguration.subscription || {}),
                gst_percentage: value,
              };
            }
          }
        )

        .addCase(
          updateSubscriptionBillingConfigThunk.rejected,
          (
            state,
            action
          ) => {
            state.savingSubscriptionConfig = false;
            state.billingConfigurationError =
              action.payload;
          }
        )

        // ====================================================
        // BILLING CONFIGURATION - SCHEDULER
        // ====================================================

        .addCase(
          updateBillingSchedulerThunk.pending,
          (state) => {
            state.savingSchedulerConfig = true;
            state.billingConfigurationError = null;
          }
        )

        .addCase(
          updateBillingSchedulerThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.savingSchedulerConfig = false;

            const value =
              action.payload?.data?.subscription_cron_time;

            if (value !== undefined) {
              state.billingConfiguration =
                state.billingConfiguration || {};

              state.billingConfiguration.scheduler = {
                ...(state.billingConfiguration.scheduler || {}),
                subscription_cron_time: value,
              };
            }
          }
        )

        .addCase(
          updateBillingSchedulerThunk.rejected,
          (
            state,
            action
          ) => {
            state.savingSchedulerConfig = false;
            state.billingConfigurationError =
              action.payload;
          }
        )

        // ====================================================
        // BILLING CONFIGURATION - INVOICE
        // ====================================================

        .addCase(
          updateInvoiceBillingConfigThunk.pending,
          (state) => {
            state.savingInvoiceConfig = true;
            state.billingConfigurationError = null;
          }
        )

        .addCase(
          updateInvoiceBillingConfigThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.savingInvoiceConfig = false;

            const value =
              action.payload?.data?.invoice_prefix;

            if (value !== undefined) {
              state.billingConfiguration =
                state.billingConfiguration || {};

              state.billingConfiguration.invoice = {
                ...(state.billingConfiguration.invoice || {}),
                invoice_prefix: value,
              };
            }
          }
        )

        .addCase(
          updateInvoiceBillingConfigThunk.rejected,
          (
            state,
            action
          ) => {
            state.savingInvoiceConfig = false;
            state.billingConfigurationError =
              action.payload;
          }
        )

        // ====================================================
        // BILLING CONFIGURATION - MANUAL TAX
        // ====================================================

        .addCase(
          updateManualTaxBillingConfigThunk.pending,
          (
            state,
            action
          ) => {
            const category =
              action.meta.arg?.category;

            if (category) {
              state.savingManualTaxConfig[category] = true;
            }

            state.billingConfigurationError = null;
          }
        )

        .addCase(
          updateManualTaxBillingConfigThunk.fulfilled,
          (
            state,
            action
          ) => {
            const category =
              action.payload?.category;

            if (category) {
              state.savingManualTaxConfig[category] = false;

              const data =
                action.payload?.data;

              if (data) {
                state.billingConfiguration =
                  state.billingConfiguration || {};

                state.billingConfiguration.manual_tax = {
                  ...(state.billingConfiguration.manual_tax || {}),
                  [category]: data,
                };
              }
            }
          }
        )

        .addCase(
          updateManualTaxBillingConfigThunk.rejected,
          (
            state,
            action
          ) => {
            const category =
              action.meta.arg?.category;

            if (category) {
              state.savingManualTaxConfig[category] = false;
            }

            state.billingConfigurationError =
              action.payload;
          }
        )

        // ====================================================
        // BILLING CONFIGURATION - ADD SETTING
        // ====================================================

        .addCase(
          addBillingSettingThunk.pending,
          (state) => {
            state.addingBillingSetting = true;
            state.billingConfigurationError = null;
          }
        )

        .addCase(
          addBillingSettingThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.addingBillingSetting = false;

            const data =
              action.payload?.data;

            if (data) {
              state.billingConfiguration =
                state.billingConfiguration || {};

              const current =
                state.billingConfiguration
                  .additional_settings || [];

              state.billingConfiguration.additional_settings = [
                ...current,
                data,
              ].sort((a, b) =>
                String(a.setting_key).localeCompare(
                  String(b.setting_key)
                )
              );
            }
          }
        )

        .addCase(
          addBillingSettingThunk.rejected,
          (
            state,
            action
          ) => {
            state.addingBillingSetting = false;
            state.billingConfigurationError =
              action.payload;
          }
        )

        // ====================================================
        // BILLING CONFIGURATION - UPDATE SETTING
        // ====================================================

        .addCase(
          updateBillingSettingThunk.pending,
          (
            state,
            action
          ) => {
            const settingId =
              action.meta.arg?.settingId;

            if (settingId) {
              state.savingAdditionalSettings[settingId] = true;
            }

            state.billingConfigurationError = null;
          }
        )

        .addCase(
          updateBillingSettingThunk.fulfilled,
          (
            state,
            action
          ) => {
            const data =
              action.payload?.data;

            const settingId =
              data?.setting_id;

            if (settingId) {
              state.savingAdditionalSettings[settingId] = false;

              state.billingConfiguration =
                state.billingConfiguration || {};

              const current =
                state.billingConfiguration
                  .additional_settings || [];

              state.billingConfiguration.additional_settings =
                current.map((item) =>
                  Number(item.setting_id) === Number(settingId)
                    ? data
                    : item
                );
            }
          }
        )

        .addCase(
          updateBillingSettingThunk.rejected,
          (
            state,
            action
          ) => {
            const settingId =
              action.meta.arg?.settingId;

            if (settingId) {
              state.savingAdditionalSettings[settingId] = false;
            }

            state.billingConfigurationError =
              action.payload;
          }
        );
    },
  });

// ============================================================
// EXPORT
// ============================================================

export const {
  clearBillingError,
  clearSelectedBill,
  clearReceiptHistory,
  resetPanchayathBilling,
  clearBillingConfigurationError,
} =
  panchayathBillingSlice.actions;

export default
  panchayathBillingSlice.reducer;